import { Chip, Dialog, Divider, Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Button from "../Button";
import { ConnectedButtonGroup } from "../ConnectedButtonGroup";
import DialogWrapper from "../DialogWrapper";
import InfoAlert from "../InfoAlert";
import {
	HideFutureCheckbox,
	type HideFutureConfig,
} from "./HideFutureCheckbox";
import { StrategyList } from "./StrategyList";

export type { HideFutureConfig };

export type AboutQuestionLevel = {
	value: string;
	label: string;
};

type Props = {
	visible: boolean;
	onClose: () => void;
	title: string;
	strategies: string[];
	levels?: AboutQuestionLevel[];
	selectedLevel?: string;
	onSelectLevel?: (value: string) => void;
	supportingText?: string;
	showDivider?: boolean;
	tip?: string;
	primaryActionLabel?: string;
	onPrimaryAction?: () => void;
	maxWidth?: number;
	hideFutureConfig?: HideFutureConfig;
};

const AboutQuestionDialog = ({
	visible,
	onClose,
	title,
	strategies,
	levels = [],
	selectedLevel,
	onSelectLevel,
	supportingText,
	showDivider = true,
	tip,
	primaryActionLabel = "Got it",
	onPrimaryAction,
	maxWidth = 440,
	hideFutureConfig,
}: Props) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();

	const handlePrimary = onPrimaryAction ?? onClose;

	const activeLevel =
		levels.find((level) => level.value === selectedLevel) ??
		(selectedLevel
			? { value: selectedLevel, label: selectedLevel }
			: undefined);

	return (
		<DialogWrapper
			visible={visible}
			handleClose={onClose}
			width={isMobile ? "90%" : maxWidth}
			maxWidth={maxWidth}
		>
			<Dialog.Icon icon="information-outline" />
			<Dialog.Title style={{ textAlign: "center" }}>{title}</Dialog.Title>
			<Dialog.Content
				style={{
					gap: 16,
				}}
			>
				{levels.length && onSelectLevel ? (
					<ConnectedButtonGroup
						density="small"
						showSelectedCheck
						value={selectedLevel ?? ""}
						onValueChange={(value) => onSelectLevel(value as string)}
						buttons={levels.map((level) => ({
							value: level.value,
							label: level.label,
							showSelectedCheck: selectedLevel === level.value,
						}))}
					/>
				) : activeLevel ? (
					<Chip
						compact
						style={{
							alignSelf: "center",
						}}
					>
						{activeLevel.label}
					</Chip>
				) : null}

				{supportingText ? (
					<Text
						variant="bodyLarge"
						style={{
							color: theme.colors.onSurfaceVariant,
							textAlign: "center",
						}}
					>
						{supportingText}
					</Text>
				) : null}

				{showDivider ? <Divider style={{ width: "100%" }} /> : null}

				<StrategyList strategies={strategies} />

				{tip ? (
					<InfoAlert
						text={tip}
						icon="lightbulb-outline"
						backgroundColor={theme.colors.surfaceVariant}
						borderWidth={1}
						borderColor={theme.colors.outlineVariant}
						width="100%"
					/>
				) : null}
			</Dialog.Content>
			<Divider />

			<Dialog.Actions
				style={{
					paddingTop: 16,
					justifyContent: hideFutureConfig ? "space-between" : "flex-end",
				}}
			>
				<HideFutureCheckbox config={hideFutureConfig} />
				<Button onPress={handlePrimary}>{primaryActionLabel}</Button>
			</Dialog.Actions>
		</DialogWrapper>
	);
};

export default AboutQuestionDialog;
